import type { EventName, OutgoingEvent, SdkConfig, SdkConfigInput } from './types';
import { uuid, nowMs } from './utils';
import { redactUrl } from './redact';
import { log, setDebug } from './log';
import type { Identity } from './identity';
import type { Consent } from './consent';
import type { Queue } from './queue';
import type { Optin, OptinSource } from './optin';
import { isEcommerce, validateEcommerce } from '../ecommerce/validator';

/**
 * The thing exposed as `window.emblue` once the SDK has loaded.
 *
 * Two call shapes are equivalent:
 *   emblue('track', 'eventName', { props });
 *   emblue.track('eventName', { props });
 */
export interface Api {
	(method: string, ...args: unknown[]): unknown;
	/** Installed by the SDK entry point right after `buildApi` (see `index.ts`):
	 *  a post-load `init` only merges config, so it needs the live config object
	 *  the entry point owns. Accepts the account write key or a config object. */
	init(config?: string | SdkConfigInput): void;
	track(name: EventName, properties?: Record<string, unknown>): void;
	tag(tagName: string, properties?: Record<string, unknown>): void;
	untag(tagName: string): void;
	/** `optinSource` only labels the resulting `optin` event. The platform adapters
	 *  pass their own name so a VTeX/Shopify session login is distinguishable from
	 *  a hand-written `identify()` call; sites can leave it out. */
	identify(externalUserId: string, traits?: Record<string, unknown>, optinSource?: OptinSource): void;
	setEmail(email: string, optinSource?: OptinSource): Promise<string | null>;
	/** Declares an opt-in for a capture the SDK can't see — a newsletter widget
	 *  that isn't a `<form>`, a modal, a native-app webview. `source` is free-form;
	 *  the auto-tracked ones are login/register/newsletter/form/datalayer. Deduped
	 *  per (identifier, source) like every other opt-in, and a no-op until an
	 *  identifier exists (call `setEmail`/`identify` first). */
	optin(source?: OptinSource, properties?: Record<string, unknown>): void;
	page(properties?: { url?: string; title?: string; referrer?: string }): void;
	consent(state: { analytics?: boolean; marketing?: boolean }): void;
	flush(): Promise<void>;
	reset(): void;
	ready(callback: () => void): void;
	debug(enabled: boolean): void;
	eraseMe(): Promise<void>;
	q?: unknown[];
}

const readyCallbacks: Array<() => void> = [];
let readyFired = false;

export function fireReady(): void {
	readyFired = true;
	const list = readyCallbacks.splice(0);
	for (const cb of list) {
		try {
			cb();
		} catch (e) {
			log('ready callback threw', e);
		}
	}
}

export function buildApi(config: SdkConfig, identity: Identity, consent: Consent, queue: Queue, optin: Optin): Api {
	const makeEvent = (name: EventName, properties: Record<string, unknown> = {}): OutgoingEvent => ({
		event_id: uuid(),
		name,
		timestamp: nowMs(),
		properties,
	});

	const methods = {
		track(name: EventName, properties?: Record<string, unknown>) {
			if (!name || typeof name !== 'string') {
				log('track(): name must be a non-empty string');
				return;
			}
			let props = properties || {};
			if (isEcommerce(name)) {
				props = validateEcommerce(name, props);
			}
			queue.enqueue(makeEvent(name, props));
		},

		tag(tagName: string, properties?: Record<string, unknown>) {
			if (!tagName || typeof tagName !== 'string') return;
			queue.enqueue(
				makeEvent('custom_tag', {
					...(properties || {}),
					tag: tagName,
					op: 'add',
				}),
			);
		},

		untag(tagName: string) {
			if (!tagName || typeof tagName !== 'string') return;
			queue.enqueue(makeEvent('custom_tag', { tag: tagName, op: 'remove' }));
		},

		identify(externalUserId: string, traits?: Record<string, unknown>, optinSource: OptinSource = 'identify') {
			if (!externalUserId || typeof externalUserId !== 'string') return;
			// Extract email from traits and hash it client-side. The
			// post-hash event below picks up identity.emailSha256 from the
			// envelope on the next batch, so order-of-resolution doesn't
			// matter from the caller's point of view.
			const email = traits && typeof traits.email === 'string' ? (traits.email as string) : undefined;
			identity.identify(externalUserId, traits);
			queue.enqueue(
				makeEvent('identify', {
					external_user_id: externalUserId,
					traits: identity.traits,
				}),
			);
			if (email) {
				// Fire-and-forget — hash + cookie write + listener fan-out
				// happen async. Subsequent batches will carry email_sha256.
				// The opt-in waits for the hash so it announces both identifiers
				// rather than just the external id.
				identity
					.setEmail(email)
					.then(() => optin.record(optinSource))
					.catch(() => {
						/* swallowed by inner log */
					});
			} else {
				optin.record(optinSource);
			}
		},

		setEmail(email: string, optinSource: OptinSource = 'api'): Promise<string | null> {
			return identity.setEmail(email).then((hash) => {
				if (hash) optin.record(optinSource);
				return hash;
			});
		},

		optin(source?: OptinSource, properties?: Record<string, unknown>) {
			optin.record(source || 'api', properties);
		},

		page(properties?: { url?: string; title?: string; referrer?: string }) {
			const p = properties || {};
			queue.enqueue(
				makeEvent('page_view', {
					url: redactUrl(p.url || (typeof location !== 'undefined' ? location.href : '')),
					url_title: p.title || (typeof document !== 'undefined' ? document.title : ''),
					referrer: redactUrl(p.referrer || (typeof document !== 'undefined' ? document.referrer : '')),
					navigation_type: 'manual',
				}),
			);
		},

		consent(state: { analytics?: boolean; marketing?: boolean }) {
			consent.set(state);
		},

		flush(): Promise<void> {
			return queue.flush();
		},

		reset() {
			identity.reset();
			optin.reset();
		},

		ready(callback: () => void) {
			if (typeof callback !== 'function') return;
			if (readyFired) {
				try {
					callback();
				} catch (e) {
					log('ready callback threw', e);
				}
			} else {
				readyCallbacks.push(callback);
			}
		},

		debug(enabled: boolean) {
			setDebug(!!enabled);
			config.debug = !!enabled;
		},

		eraseMe(): Promise<void> {
			identity.reset();
			optin.reset();
			return Promise.resolve();
		},
	};

	const fn: any = function (method: string, ...rest: unknown[]) {
		if (typeof (methods as any)[method] !== 'function') {
			log('unknown method', method);
			return undefined;
		}
		return (methods as any)[method](...rest);
	};
	Object.assign(fn, methods);
	return fn as Api;
}
